import {
  Box,
  CircularProgress,
  Alert,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useGetDashboardQuery } from '../api/dashboardApi';

type Props = {
  blockKey: string;
  title?: string | null;
  relatedLinkUrl?: string | null;
  relatedLinkCaption?: string | null;
};

/**
 * Turns a raw SQL alias / JSON key into a human-readable column label.
 * Works for any PascalCase or camelCase key, e.g.:
 *   TaskName   -> Task Name
 *   ProjectId  -> Project Id
 *   UpdatedAt  -> Updated At
 */
function humanizeKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // split camel/Pascal boundaries
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2') // split consecutive caps (e.g. IDNumber)
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/**
 * Technical/service fields (typically ids used only for links or sorting)
 * are hidden from the rendered table by default, without touching the SQL
 * or backend response. Matches common suffixes: Id, Key.
 */
const HIDDEN_FIELD_PATTERN = /(Id|Key)$/;

function getVisibleColumns(columns: string[]): string[] {
  return columns.filter((col) => !HIDDEN_FIELD_PATTERN.test(col));
}

// Matches ISO-like datetime strings returned by the backend, e.g. 2026-07-08T10:54:00.207
const ISO_DATETIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;

/**
 * Formats an ISO datetime string into "dd.MM.yyyy HH:mm".
 * Falls back to the raw value if parsing fails.
 */
function formatIsoDateTime(value: string): string {
  const raw = value.trim();
  const hasTimeZone = /[zZ]$/.test(raw) || /[+-]\d{2}:\d{2}$/.test(raw);
  const date = new Date(hasTimeZone ? raw : `${raw}Z`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const pad = (n: number) => String(n).padStart(2, '0');

  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());

  return `${day}.${month}.${year} ${hours}:${minutes}`;
}

/**
 * Renders a cell value: detects ISO datetime strings and formats them
 * as dd.MM.yyyy HH:mm, otherwise returns the value as plain text.
 */
function formatCellValue(value: unknown): string {
  if (typeof value === 'string' && ISO_DATETIME_PATTERN.test(value)) {
    return formatIsoDateTime(value);
  }
  return String(value ?? '');
}

export const ActivityBlockSection = ({ blockKey, relatedLinkUrl, relatedLinkCaption }: Props) => {
  const { data: rows, isLoading, isError } = useGetDashboardQuery(blockKey);

  const relatedLink =
    relatedLinkUrl && relatedLinkUrl !== '#' ? (
      <Link href={relatedLinkUrl} underline="hover" sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
        {relatedLinkCaption?.trim() || 'Open'}
      </Link>
    ) : null;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (isError) {
    return <Alert severity="error">Failed to load activity data</Alert>;
  }

  if (!rows?.length) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
          No data
        </Typography>
        {relatedLink}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {rows.map((row, idx) => {
        const allColumns = row.data.length > 0 ? Object.keys(row.data[0]) : [];
        const columns = getVisibleColumns(allColumns);

        return (
          <Box key={idx}>
            {row.caption && (
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                {row.caption}
              </Typography>
            )}
            {row.data.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No data
              </Typography>
            ) : (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  maxWidth: '100%',
                  overflowX: 'auto',
                }}
              >
                <Table
                  size="small"
                  sx={{
                    minWidth: { xs: 'max-content', sm: 'auto' },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {columns.map((col) => (
                        <TableCell key={col} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {humanizeKey(col)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {row.data.map((record, rowIdx) => (
                      <TableRow key={rowIdx} hover>
                        {columns.map((col) => (
                          <TableCell key={col} sx={{ whiteSpace: { xs: 'nowrap', sm: 'normal' } }}>
                            {formatCellValue(record[col])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        );
      })}
      {relatedLink}
    </Box>
  );
};
